import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  Collapse,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import DevicesIcon from "@mui/icons-material/Devices";
import ComputerIcon from "@mui/icons-material/Computer";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import TabletIcon from "@mui/icons-material/Tablet";
import WarningIcon from "@mui/icons-material/Warning";
import PersonIcon from "@mui/icons-material/Person";
import RefreshIcon from "@mui/icons-material/Refresh";

const ChatFingerprintingSection = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [userAgents, setUserAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uaLoading, setUaLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [uaPage, setUaPage] = useState(1);
  const [uaTotalPages, setUaTotalPages] = useState(1);
  const [expandedFingerprint, setExpandedFingerprint] = useState(null);
  const [fingerprintGuests, setFingerprintGuests] = useState([]);
  const [fpLoading, setFpLoading] = useState(false);
  const [uaDetailDialog, setUaDetailDialog] = useState({ open: false, ua: null, guests: [] });

  useEffect(() => {
    fetchAnalytics();
    fetchUserAgents();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/fingerprinting/analytics`
      );
      setAnalytics(response.data);
    } catch (err) {
      console.error("Failed to fetch analytics:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserAgents = async (searchTerm = "", page = 1) => {
    setUaLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (searchTerm) params.append("search", searchTerm);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/fingerprinting/user-agents?${params}`
      );
      setUserAgents(response.data.userAgents);
      setUaTotalPages(response.data.totalPages);
      setUaPage(page);
    } catch (err) {
      console.error("Failed to fetch user agents:", err);
    } finally {
      setUaLoading(false);
    }
  };

  const fetchFingerprintGuests = async (hash) => {
    if (expandedFingerprint === hash) {
      setExpandedFingerprint(null);
      return;
    }

    setFpLoading(true);
    setExpandedFingerprint(hash);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/fingerprinting/fingerprint/${hash}`
      );
      setFingerprintGuests(response.data.guests);
    } catch (err) {
      console.error("Failed to fetch fingerprint guests:", err);
    } finally {
      setFpLoading(false);
    }
  };

  const fetchUAGuests = async (ua) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/fingerprinting/by-user-agent?ua=${encodeURIComponent(ua)}`
      );
      setUaDetailDialog({ open: true, ua, guests: response.data.guests });
    } catch (err) {
      console.error("Failed to fetch UA guests:", err);
    }
  };

  const handleSearch = () => {
    fetchUserAgents(search, 1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleDateString();
  };

  const parseUserAgent = (ua) => {
    if (!ua) return { browser: "Unknown", os: "Unknown", device: "Unknown" };

    let browser = "Other";
    let os = "Other";
    let device = "Desktop";

    // Browser detection
    if (ua.includes("Edg/")) browser = "Edge";
    else if (ua.includes("OPR/") || ua.includes("Opera")) browser = "Opera";
    else if (ua.includes("Chrome/")) browser = "Chrome";
    else if (ua.includes("Firefox/")) browser = "Firefox";
    else if (ua.includes("Safari/") && !ua.includes("Chrome")) browser = "Safari";

    // OS detection
    if (ua.includes("Windows")) os = "Windows";
    else if (ua.includes("Mac OS") || ua.includes("Macintosh")) os = "macOS";
    else if (ua.includes("Android")) os = "Android";
    else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";
    else if (ua.includes("Linux")) os = "Linux";

    // Device detection
    if (ua.includes("Mobile") || ua.includes("Android") || ua.includes("iPhone")) device = "Mobile";
    else if (ua.includes("Tablet") || ua.includes("iPad")) device = "Tablet";

    return { browser, os, device };
  };

  const getDeviceIcon = (device) => {
    switch (device) {
      case "Mobile": return <PhoneAndroidIcon sx={{ fontSize: 16 }} />;
      case "Tablet": return <TabletIcon sx={{ fontSize: 16 }} />;
      default: return <ComputerIcon sx={{ fontSize: 16 }} />;
    }
  };

  const getBrowserColor = (browser) => {
    switch (browser) {
      case "Chrome": return "#4285f4";
      case "Firefox": return "#ff7139";
      case "Safari": return "#0fb5ee";
      case "Edge": return "#0078d7";
      case "Opera": return "#ff1b2d";
      default: return "#9e9e9e";
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress sx={{ color: "#fff" }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin/chatroom/guests")}
          sx={{ color: "#9e9e9e", mr: 2 }}
        >
          Back to Guests
        </Button>
        <FingerprintIcon sx={{ color: "#ff9800", mr: 1 }} />
        <Typography variant="h5" sx={{ color: "#fff" }}>
          Fingerprinting Analytics
        </Typography>
        <IconButton onClick={() => { fetchAnalytics(); fetchUserAgents(); }} sx={{ ml: 2, color: "#9e9e9e" }}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Stats Cards */}
      {analytics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, backgroundColor: "#16213e", border: "1px solid #394867", textAlign: "center" }}>
              <Typography variant="h4" sx={{ color: "#4caf50", fontWeight: 700 }}>
                {analytics.stats.total_guests}
              </Typography>
              <Typography variant="body2" sx={{ color: "#9e9e9e" }}>Total Guests</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, backgroundColor: "#16213e", border: "1px solid #394867", textAlign: "center" }}>
              <Typography variant="h4" sx={{ color: "#2196f3", fontWeight: 700 }}>
                {analytics.stats.unique_fingerprints}
              </Typography>
              <Typography variant="body2" sx={{ color: "#9e9e9e" }}>Unique Fingerprints</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, backgroundColor: "#16213e", border: "1px solid #394867", textAlign: "center" }}>
              <Typography variant="h4" sx={{ color: "#ff9800", fontWeight: 700 }}>
                {analytics.stats.unique_user_agents}
              </Typography>
              <Typography variant="body2" sx={{ color: "#9e9e9e" }}>Unique User Agents</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, backgroundColor: "#16213e", border: "1px solid #394867", textAlign: "center" }}>
              <Typography variant="h4" sx={{ color: "#f44336", fontWeight: 700 }}>
                {analytics.duplicateFingerprints?.length || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: "#9e9e9e" }}>Multi-Account Fingerprints</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Browser, OS, Device Breakdown */}
      {analytics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Browsers */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, backgroundColor: "#16213e", border: "1px solid #394867" }}>
              <Typography variant="subtitle2" sx={{ color: "#9e9e9e", mb: 2 }}>Browsers</Typography>
              {analytics.browsers.map((b) => (
                <Box key={b.browser} sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: getBrowserColor(b.browser) }} />
                    <Typography variant="body2" sx={{ color: "#fff" }}>{b.browser}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: "#9e9e9e" }}>{b.count}</Typography>
                </Box>
              ))}
            </Paper>
          </Grid>

          {/* Operating Systems */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, backgroundColor: "#16213e", border: "1px solid #394867" }}>
              <Typography variant="subtitle2" sx={{ color: "#9e9e9e", mb: 2 }}>Operating Systems</Typography>
              {analytics.operatingSystems.map((os) => (
                <Box key={os.os} sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2" sx={{ color: "#fff" }}>{os.os}</Typography>
                  <Typography variant="body2" sx={{ color: "#9e9e9e" }}>{os.count}</Typography>
                </Box>
              ))}
            </Paper>
          </Grid>

          {/* Device Types */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, backgroundColor: "#16213e", border: "1px solid #394867" }}>
              <Typography variant="subtitle2" sx={{ color: "#9e9e9e", mb: 2 }}>Device Types</Typography>
              {analytics.deviceTypes.map((d) => (
                <Box key={d.device_type} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {getDeviceIcon(d.device_type)}
                    <Typography variant="body2" sx={{ color: "#fff" }}>{d.device_type}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: "#9e9e9e" }}>{d.count}</Typography>
                </Box>
              ))}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Duplicate Fingerprints - Potential Multi-Account Users */}
      {analytics?.duplicateFingerprints?.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: "#16213e", border: "1px solid #f44336" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <WarningIcon sx={{ color: "#f44336" }} />
            <Typography variant="subtitle1" sx={{ color: "#f44336" }}>
              Potential Multi-Account Users (Same Fingerprint)
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Fingerprint</TableCell>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Accounts</TableCell>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Usernames</TableCell>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>First Created</TableCell>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Last Seen</TableCell>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analytics.duplicateFingerprints.map((fp) => (
                  <React.Fragment key={fp.fingerprint_hash}>
                    <TableRow hover sx={{ "&:hover": { backgroundColor: "#1a1a2e" } }}>
                      <TableCell sx={{ color: "#fff", fontFamily: "monospace", fontSize: "0.75rem", borderBottom: "1px solid #394867" }}>
                        {fp.fingerprint_hash.substring(0, 16)}...
                      </TableCell>
                      <TableCell sx={{ borderBottom: "1px solid #394867" }}>
                        <Chip
                          label={fp.account_count}
                          size="small"
                          sx={{ backgroundColor: fp.account_count > 3 ? "#f4433622" : "#ff980022", color: fp.account_count > 3 ? "#f44336" : "#ff9800" }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", maxWidth: 200 }}>
                        {fp.usernames.slice(0, 3).join(", ")}
                        {fp.usernames.length > 3 && ` +${fp.usernames.length - 3} more`}
                      </TableCell>
                      <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", fontSize: "0.8rem" }}>
                        {formatDate(fp.first_created)}
                      </TableCell>
                      <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", fontSize: "0.8rem" }}>
                        {formatDate(fp.last_seen)}
                      </TableCell>
                      <TableCell sx={{ borderBottom: "1px solid #394867" }}>
                        <IconButton
                          size="small"
                          onClick={() => fetchFingerprintGuests(fp.fingerprint_hash)}
                          sx={{ color: "#2196f3" }}
                        >
                          {expandedFingerprint === fp.fingerprint_hash ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ p: 0, borderBottom: expandedFingerprint === fp.fingerprint_hash ? "1px solid #394867" : "none" }}>
                        <Collapse in={expandedFingerprint === fp.fingerprint_hash}>
                          <Box sx={{ p: 2, backgroundColor: "#0f1729" }}>
                            {fpLoading ? (
                              <CircularProgress size={20} sx={{ color: "#fff" }} />
                            ) : (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ color: "#666", borderBottom: "1px solid #394867" }}>Username</TableCell>
                                    <TableCell sx={{ color: "#666", borderBottom: "1px solid #394867" }}>Gender</TableCell>
                                    <TableCell sx={{ color: "#666", borderBottom: "1px solid #394867" }}>Messages</TableCell>
                                    <TableCell sx={{ color: "#666", borderBottom: "1px solid #394867" }}>Status</TableCell>
                                    <TableCell sx={{ color: "#666", borderBottom: "1px solid #394867" }}>Created</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {fingerprintGuests.map((g) => (
                                    <TableRow key={g.id}>
                                      <TableCell sx={{ borderBottom: "1px solid #394867" }}>
                                        <Typography
                                          component={Link}
                                          to={`/admin/chatroom/user/${encodeURIComponent(g.username)}`}
                                          sx={{ color: "#2196f3", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                                        >
                                          {g.username}
                                        </Typography>
                                      </TableCell>
                                      <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", textTransform: "capitalize" }}>
                                        {g.gender || "-"}
                                      </TableCell>
                                      <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>{g.message_count}</TableCell>
                                      <TableCell sx={{ borderBottom: "1px solid #394867" }}>
                                        <Chip
                                          label={g.is_banned ? "Banned" : "Active"}
                                          size="small"
                                          sx={{ backgroundColor: g.is_banned ? "#f4433622" : "#4caf5022", color: g.is_banned ? "#f44336" : "#4caf50" }}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ color: "#666", borderBottom: "1px solid #394867", fontSize: "0.8rem" }}>
                                        {formatDate(g.created_at)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* User Agent Search & List */}
      <Paper sx={{ p: 2, backgroundColor: "#16213e", border: "1px solid #394867" }}>
        <Typography variant="subtitle1" sx={{ color: "#fff", mb: 2 }}>
          <DevicesIcon sx={{ mr: 1, verticalAlign: "middle" }} />
          User Agent Analysis
        </Typography>

        {/* Search */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search user agents (e.g., Chrome, iPhone, Windows)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                color: "#fff",
                "& fieldset": { borderColor: "#394867" },
                "&:hover fieldset": { borderColor: "#541b14" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9e9e9e" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="outlined" onClick={handleSearch} sx={{ color: "#fff", borderColor: "#394867" }}>
            Search
          </Button>
        </Box>

        {/* User Agent Table */}
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", borderBottom: "1px solid #394867" }}>User Agent</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Browser</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", borderBottom: "1px solid #394867" }}>OS</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Device</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Guests</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Last Active</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", borderBottom: "1px solid #394867" }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {uaLoading ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: "center", borderBottom: "1px solid #394867" }}>
                    <CircularProgress size={24} sx={{ color: "#fff" }} />
                  </TableCell>
                </TableRow>
              ) : (
                userAgents.map((ua, idx) => {
                  const parsed = parseUserAgent(ua.user_agent);
                  return (
                    <TableRow key={idx} hover sx={{ "&:hover": { backgroundColor: "#1a1a2e" } }}>
                      <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <Tooltip title={ua.user_agent}>
                          <span style={{ fontSize: "0.75rem" }}>{ua.user_agent?.substring(0, 60)}...</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ borderBottom: "1px solid #394867" }}>
                        <Chip
                          label={parsed.browser}
                          size="small"
                          sx={{ backgroundColor: `${getBrowserColor(parsed.browser)}22`, color: getBrowserColor(parsed.browser) }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>{parsed.os}</TableCell>
                      <TableCell sx={{ borderBottom: "1px solid #394867" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#9e9e9e" }}>
                          {getDeviceIcon(parsed.device)}
                          {parsed.device}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ borderBottom: "1px solid #394867" }}>
                        <Chip
                          label={ua.guest_count}
                          size="small"
                          sx={{ backgroundColor: ua.guest_count > 1 ? "#ff980022" : "#4caf5022", color: ua.guest_count > 1 ? "#ff9800" : "#4caf50" }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: "#666", borderBottom: "1px solid #394867", fontSize: "0.8rem" }}>
                        {formatDate(ua.last_active)}
                      </TableCell>
                      <TableCell sx={{ borderBottom: "1px solid #394867" }}>
                        <Tooltip title="View Guests">
                          <IconButton size="small" onClick={() => fetchUAGuests(ua.user_agent)} sx={{ color: "#2196f3" }}>
                            <PersonIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {uaTotalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", gap: 2, pt: 2, borderTop: "1px solid #394867", mt: 2 }}>
            <Button
              size="small"
              disabled={uaPage === 1}
              onClick={() => fetchUserAgents(search, uaPage - 1)}
              sx={{ color: "#fff" }}
            >
              Previous
            </Button>
            <Typography variant="body2" sx={{ color: "#9e9e9e", lineHeight: "30px" }}>
              Page {uaPage} of {uaTotalPages}
            </Typography>
            <Button
              size="small"
              disabled={uaPage === uaTotalPages}
              onClick={() => fetchUserAgents(search, uaPage + 1)}
              sx={{ color: "#fff" }}
            >
              Next
            </Button>
          </Box>
        )}
      </Paper>

      {/* User Agent Detail Dialog */}
      <Dialog
        open={uaDetailDialog.open}
        onClose={() => setUaDetailDialog({ open: false, ua: null, guests: [] })}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { backgroundColor: "#1a1a2e", border: "1px solid #394867" } }}
      >
        <DialogTitle sx={{ color: "#fff", borderBottom: "1px solid #394867" }}>
          Guests with User Agent
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ color: "#666", mb: 2, fontFamily: "monospace", fontSize: "0.75rem", wordBreak: "break-all" }}>
            {uaDetailDialog.ua}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Username</TableCell>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Gender</TableCell>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Messages</TableCell>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Status</TableCell>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {uaDetailDialog.guests.map((g) => (
                  <TableRow key={g.id}>
                    <TableCell sx={{ borderBottom: "1px solid #394867" }}>
                      <Typography
                        component={Link}
                        to={`/admin/chatroom/user/${encodeURIComponent(g.username)}`}
                        target="_blank"
                        sx={{ color: "#2196f3", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                      >
                        {g.username}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", textTransform: "capitalize" }}>
                      {g.gender || "-"}
                    </TableCell>
                    <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>{g.message_count}</TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #394867" }}>
                      <Chip
                        label={g.is_banned ? "Banned" : "Active"}
                        size="small"
                        sx={{ backgroundColor: g.is_banned ? "#f4433622" : "#4caf5022", color: g.is_banned ? "#f44336" : "#4caf50" }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: "#666", borderBottom: "1px solid #394867", fontSize: "0.8rem" }}>
                      {formatDate(g.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ChatFingerprintingSection;
